import React, { useState } from 'react';
import { X, Loader2, Mail, Eye, Edit3, AlertTriangle } from 'lucide-react';
import type { TeamRole } from '../../types/teams';

export interface InviteResult {
  emailSent: boolean;
  emailError?: string;
}

interface InviteMemberModalProps {
  teamName: string;
  onClose: () => void;
  onInvite: (email: string, role: TeamRole, message?: string) => Promise<InviteResult | void>;
}

export function InviteMemberModal({ teamName, onClose, onInvite }: InviteMemberModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamRole>('viewer');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The invite row is created even when the email fails to send, so a delivery
  // failure is a warning to acknowledge — not an error, and not a silent success.
  const [warning, setWarning] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      setLoading(true);
      setError(null);
      const result = await onInvite(email.trim(), role, message.trim() || undefined);
      if (result && !result.emailSent) {
        setWarning(
          `${email.trim()} was added to the team, but the invite email could not be delivered` +
          `${result.emailError ? `: ${result.emailError}` : '.'} ` +
          `They can still accept it from their Teams page after signing in with that address. ` +
          `To retry the email, remove them from the team and invite again.`
        );
        return;
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-sand/20">
          <div>
            <h2 className="text-xl font-semibold text-midnight">Invite to {teamName}</h2>
            <p className="text-sm text-slate mt-0.5">They'll receive an email invite link</p>
          </div>
          <button onClick={onClose} className="p-1 text-slate hover:text-midnight rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-crimson/10 text-crimson p-3 rounded-lg text-sm">{error}</div>
          )}

          {warning && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded-lg text-sm flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <span>{warning}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-midnight mb-1">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="colleague@church.org"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight mb-2">Permission Level</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRole('viewer')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  role === 'viewer'
                    ? 'border-electric bg-electric/5'
                    : 'border-sand/30 hover:border-sand/60'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="h-4 w-4 text-slate" />
                  <span className="font-medium text-midnight text-sm">Viewer</span>
                </div>
                <p className="text-xs text-slate">View and duplicate sermons</p>
              </button>
              <button
                type="button"
                onClick={() => setRole('editor')}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  role === 'editor'
                    ? 'border-electric bg-electric/5'
                    : 'border-sand/30 hover:border-sand/60'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Edit3 className="h-4 w-4 text-electric" />
                  <span className="font-medium text-midnight text-sm">Editor</span>
                </div>
                <p className="text-xs text-slate">Edit content, create slides</p>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-midnight mb-1">
              Personal Message <span className="text-slate font-normal">(optional)</span>
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Add a note to your invite..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent resize-none text-sm"
            />
          </div>

          <div className="flex justify-end gap-3 pt-1">
            {warning ? (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
              >
                Done
              </button>
            ) : (
              <>
                <button type="button" onClick={onClose} className="px-4 py-2 text-slate hover:text-midnight">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send Invite
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
